import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Button, Heading } from "../../components/Common";
import { GetAppSettings, UpdateAppSettings } from "../../Service/AppSettings.service";
import { notificationSuccess } from "../../state/Action/NotificationAction";
import { useDispatch } from "react-redux";
import { FiSmartphone, FiDownloadCloud, FiAlertCircle } from "react-icons/fi";

const AppSettings = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    platform: "android",
    minVersionCode: 29,
    latestVersionCode: 29,
    minVersionName: "1.4.8",
    latestVersionName: "1.4.8",
    forceUpdate: true,
    updateUrl: "https://www.indusappstore.com/app/details?package=com.app.metrixproject",
    title: "Update Required",
    message: "A new version of Metrix is available on Indus Appstore. Please update the application to continue.",
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await GetAppSettings("android");
      if (res?.status && res?.data) {
        setFormData({
          platform: res.data.platform || "android",
          minVersionCode: res.data.minVersionCode ?? 29,
          latestVersionCode: res.data.latestVersionCode ?? 29,
          minVersionName: res.data.minVersionName || "1.4.8",
          latestVersionName: res.data.latestVersionName || "1.4.8",
          forceUpdate: res.data.forceUpdate !== undefined ? Boolean(res.data.forceUpdate) : true,
          updateUrl: res.data.updateUrl || "https://www.indusappstore.com/app/details?package=com.app.metrixproject",
          title: res.data.title || "Update Required",
          message: res.data.message || "",
        });
      }
    } catch (error) {
      console.error("Error fetching app settings:", error);
      dispatch(
        notificationSuccess({
          show: true,
          type: "error",
          message: "Failed to load app settings",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        platform: formData.platform,
        minVersionCode: Number(formData.minVersionCode),
        latestVersionCode: Number(formData.latestVersionCode),
        minVersionName: formData.minVersionName,
        latestVersionName: formData.latestVersionName,
        forceUpdate: Boolean(formData.forceUpdate),
        updateUrl: formData.updateUrl,
        title: formData.title,
        message: formData.message,
      };

      const res = await UpdateAppSettings(payload);
      if (res?.status) {
        dispatch(
          notificationSuccess({
            show: true,
            type: "success",
            message: res.message || "App settings updated successfully",
          })
        );
        fetchSettings();
      } else {
        dispatch(
          notificationSuccess({
            show: true,
            type: "error",
            message: res?.message || "Failed to update app settings",
          })
        );
      }
    } catch (error) {
      console.error("Error saving app settings:", error);
      dispatch(
        notificationSuccess({
          show: true,
          type: "error",
          message: "Something went wrong while saving settings",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Heading head={"App Version & Force Update Settings"} />
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={handleSave}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
                    <FiSmartphone style={{ fontSize: "22px", color: "#e53935" }} />
                    <Typography variant="h6" fontWeight={600}>
                      Android Version Configuration (Indus Appstore)
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography fontSize="13px" fontWeight={500} mb="4px">
                        Latest Version Code
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        name="latestVersionCode"
                        value={formData.latestVersionCode}
                        onChange={handleChange}
                        required
                        size="small"
                        helperText="Current release versionCode in build.gradle"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography fontSize="13px" fontWeight={500} mb="4px">
                        Latest Version Name
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="latestVersionName"
                        value={formData.latestVersionName}
                        onChange={handleChange}
                        required
                        size="small"
                        placeholder="e.g. 1.4.8"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography fontSize="13px" fontWeight={500} mb="4px">
                        Minimum Required Version Code
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        name="minVersionCode"
                        value={formData.minVersionCode}
                        onChange={handleChange}
                        required
                        size="small"
                        helperText="App versions below this will be strictly blocked"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography fontSize="13px" fontWeight={500} mb="4px">
                        Minimum Required Version Name
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="minVersionName"
                        value={formData.minVersionName}
                        onChange={handleChange}
                        required
                        size="small"
                        placeholder="e.g. 1.4.8"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: formData.forceUpdate ? "#fff3e0" : "#f5f5f5",
                          border: "1px solid",
                          borderColor: formData.forceUpdate ? "#ffe0b2" : "#e0e0e0",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.forceUpdate}
                              onChange={handleChange}
                              name="forceUpdate"
                              color="error"
                            />
                          }
                          label={
                            <Box>
                              <Typography fontWeight={600} fontSize="14px">
                                {formData.forceUpdate
                                  ? "Force Update Enabled (Mandatory)"
                                  : "Optional Update Mode"}
                              </Typography>
                              <Typography fontSize="12px" color="text.secondary">
                                {formData.forceUpdate
                                  ? "Users on older versions will see a non-dismissible dialog blocking access until they update via Indus Appstore."
                                  : "Users can choose 'Later' to continue using the app without updating immediately."}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography fontSize="13px" fontWeight={500} mb="4px">
                        Indus Appstore / Download Link URL
                      </Typography>
                      <TextField
                        fullWidth
                        type="url"
                        name="updateUrl"
                        value={formData.updateUrl}
                        onChange={handleChange}
                        required
                        size="small"
                        placeholder="https://www.indusappstore.com/app/details?package=com.app.metrixproject"
                        helperText="Tapping 'Update Now' in the mobile app redirects to this Indus Appstore URL."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography fontSize="13px" fontWeight={500} mb="4px">
                        Update Dialog Title
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        size="small"
                        placeholder="Update Required"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography fontSize="13px" fontWeight={500} mb="4px">
                        Update Dialog Message
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        size="small"
                        placeholder="Explain what is new or why users need to update..."
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="flex-end" mt={3} gap={2}>
                    <Button
                      buttonName={saving ? "Saving..." : "Save Settings"}
                      disabled={saving}
                      type="submit"
                      onClick={handleSave}
                      style={{ padding: "10px 24px" }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.08)", mb: 2 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                    <FiDownloadCloud style={{ fontSize: "18px", color: "#1976d2" }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      How Force Update Works
                    </Typography>
                  </Stack>
                  <Typography fontSize="13px" color="text.secondary" paragraph>
                    <strong>1. Version Check:</strong> Every time the Android app launches, the Splash Screen calls <code>/api/appV1/checkAppUpdate</code> with its installed <code>versionCode</code>.
                  </Typography>
                  <Typography fontSize="13px" color="text.secondary" paragraph>
                    <strong>2. Mandatory Blocking:</strong> If installed version is less than <strong>Minimum Required Version Code</strong>, or if <strong>Force Update</strong> is ON and installed version is less than <strong>Latest Version Code</strong>, the app displays a non-cancellable dialog.
                  </Typography>
                  <Typography fontSize="13px" color="text.secondary" paragraph>
                    <strong>3. Indus Appstore Redirect:</strong> Tapping "Update Now" opens the Indus Appstore application directly (or browser fallback) to update the Metrix APK.
                  </Typography>
                  <Typography fontSize="13px" color="text.secondary">
                    <strong>4. Offline Resilience:</strong> If an auditor is completely offline, the app allows local survey work and does not lock them out.
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2, bgcolor: "#f8f9fa", border: "1px dashed #cfd8dc" }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="center" gap={1} mb={1}>
                    <FiAlertCircle style={{ fontSize: "18px", color: "#f57c00" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Recommended Best Practice
                    </Typography>
                  </Stack>
                  <Typography fontSize="12px" color="text.secondary">
                    When you publish a new APK to Indus Appstore (e.g. versionCode 30), update both <strong>Latest Version Code</strong> and <strong>Minimum Required Version Code</strong> to 30 here so older builds immediately prompt users to update.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      )}
    </Box>
  );
};

export default AppSettings;
